import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import { Drawer } from '../../app/Drawer';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { FavoriteButton } from '../../components/ui/FavoriteButton';
import { SkeletonBlock, SkeletonText } from '../../components/ui/Loader';
import { copy } from '../../config/copy';
import { ApiError } from '../../lib/api';
import { usePlayer } from '../../stores/player';
import { DrawerLink } from '../../app/DrawerLink';
import { albums } from './api';

/**
 * An album, in the side drawer.
 *
 * Cover, the track list, and one button that plays the lot. The editor, the
 * reordering controls and the add-tracks dialog stay on the page - they are the
 * owner's tools, and somebody who came to *listen* is who this panel is for.
 *
 * Its rows are `DrawerLink`s, so opening a track from inside an album keeps the
 * page you originally came from as the background rather than the album. See
 * `DrawerLink` on why that matters.
 */
export function AlbumDrawer() {
  const { id = '' } = useParams();

  const play = usePlayer((s) => s.play);
  const current = usePlayer((s) => s.track);

  const { data, isPending, error } = useQuery({
    queryKey: ['album', id],
    queryFn: () => albums.get(id),
    retry: false,
  });

  const playable = (data?.tracks ?? []).filter((track) => track.status === 'ready');

  return (
    <Drawer title={data?.title ?? copy['noun.collection']} fullPath={`/albums/${id}`}>
      {isPending && (
        <div className="flex flex-col gap-5" aria-busy="true">
          <SkeletonBlock className="aspect-square w-full" />
          <SkeletonText lines={2} />
        </div>
      )}

      {error && (
        <Alert tone="error">
          {error instanceof ApiError ? error.message : 'That album could not be loaded.'}
        </Alert>
      )}

      {data && (
        <div className="flex flex-col gap-5">
          {data.artwork ? (
            <img
              src={data.artwork}
              alt=""
              className="aspect-square w-full rounded-[var(--radius-panel)] object-cover"
            />
          ) : (
            <div
              aria-hidden
              className="aspect-square w-full rounded-[var(--radius-panel)]"
              style={{
                backgroundImage: 'repeating-linear-gradient(45deg,var(--color-blank) 0 14px,var(--color-blank-alt) 14px 28px)',
              }}
            />
          )}

          <div className="flex flex-col gap-1.5">
            <h2 className="text-2xl font-bold leading-tight tracking-tight text-ink">{data.title}</h2>
            {data.owner && (
              <Link
                to={`/${data.owner.username}`}
                className="text-[15px] text-text-2 hover:text-ink hover:underline"
              >
                {data.owner.display_name}
              </Link>
            )}
            <span className="meta text-faint">
              {data.tracks.length}{' '}
              {data.tracks.length === 1 ? copy['noun.track'].toLowerCase() : copy['noun.track.plural'].toLowerCase()}
            </span>
          </div>

          {data.description && (
            <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-text-2">
              {data.description}
            </p>
          )}

          {playable.length > 0 && (
            <div>
              {/* The whole album as the queue, which is the one thing an album
                  can do that a list of tracks cannot. */}
              <Button
                data-drawer-play-album
                onClick={() =>
                  void play(
                    playable[0]!,
                    playable,
                    'collection',
                  )
                }
              >
                Play {copy['noun.collection'].toLowerCase()}
              </Button>
            </div>
          )}

          <ul className="flex flex-col gap-1 border-t border-line pt-4" data-drawer-album-tracks>
            {data.tracks.map((track, index) => (
              <li key={track.id}>
                <DrawerLink
                  to={`/tracks/${track.id}`}
                  className={`flex items-center gap-3 rounded-[var(--radius-control)] px-2 py-2.5 transition-colors hover:bg-raised ${
                    current?.id === track.id ? 'bg-raised' : ''
                  }`}
                >
                  <span className="w-5 shrink-0 text-right font-mono text-[11px] text-faint">
                    {index + 1}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm text-ink">{track.title}</span>
                  <FavoriteButton
                    trackId={track.id}
                    favorited={track.favorited}
                    size="sm"
                    className="shrink-0"
                  />
                </DrawerLink>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Drawer>
  );
}
